/**
 * Coach Team Detail page — PR-X2 (May 2026).
 *
 * Renders a single team through one of two lenses controlled by the
 * `?view=` URL param:
 *   - Practice (default) — training-focused affordances + roster placeholder
 *   - Game-day            — match-prep placeholder with disabled lineup CTA
 *
 * Same `batches` row is the source of truth in both views; only which
 * sections render differs. View mode is URL-param-only state (no DB, no
 * persistence). See ViewModeToggle for the rationale.
 *
 * Scope reminder (PR-X2):
 *   - SHELLS ONLY. No real lineup, no match metadata, no player assignment.
 *   - Roster placeholder → filled in PR-X3a (player_batches join table).
 *   - "Build lineup" disabled CTA → wired in PR-X3b (match_lineups).
 *   - Edit / Archive / Restore actions stay on the LIST page for PR-X2;
 *     moving them into the detail page is a follow-up to avoid duplicating
 *     mutation state across two surfaces.
 *
 * Security:
 *   - 404 on cross-tenant or missing id → friendly "Team not found" page.
 *   - 403 (parent direct-URL probing) → caught by parent /coach guard in
 *     App.tsx; this component will not render for non-staff roles.
 */

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Edit2,
  Archive,
  ArchiveRestore,
  Trophy,
  AlertCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { ViewModeToggle, parseViewMode } from "@/components/teams/ViewModeToggle";

interface BatchDetail {
  id: number;
  academyId: number | null;
  name: string;
  ageGroup: string;
  ageRange: string;
  skillLevel: "Beginner" | "Intermediate" | "Advanced" | null;
  coachId: number;
  schedule: string | null;
  venue: string | null;
  maxCapacity: number | null;
  currentEnrollment: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function skillBadgeColor(level?: string | null) {
  switch (level) {
    case "Beginner":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "Intermediate":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "Advanced":
      return "bg-orange-100 text-orange-700 border-orange-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

export default function TeamDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewMode = parseViewMode(searchParams.get("view"));

  const { data, isLoading, error } = useQuery<{ ok: boolean; data: BatchDetail }>({
    queryKey: ["/api/batches", id],
    queryFn: async () =>
      apiRequest("GET", `/api/batches/${id}`) as Promise<any>,
    enabled: !!id,
    // 404 should fail fast — no point retrying an existence check.
    retry: false,
  });

  if (isLoading) {
    return (
      <div
        className="min-h-screen px-4 sm:px-6 lg:px-8 py-6"
        data-testid="team-detail-loading"
      >
        <div className="max-w-5xl mx-auto space-y-4">
          <div className="h-8 w-32 bg-slate-200 rounded animate-pulse" />
          <div className="h-12 w-2/3 bg-slate-200 rounded animate-pulse" />
          <div className="h-40 w-full bg-slate-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div
        className="min-h-screen px-4 sm:px-6 lg:px-8 py-12 text-center"
        data-testid="team-detail-not-found"
      >
        <AlertCircle className="h-12 w-12 mx-auto text-slate-400 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Team not found</h1>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">
          This team doesn't exist or isn't part of your academy. The same
          response is returned whether the team belongs to another academy
          or doesn't exist at all.
        </p>
        <Button onClick={() => navigate("/coach/teams")} data-testid="btn-back-to-teams-from-404">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to teams
        </Button>
      </div>
    );
  }

  const team = data.data;

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate("/coach/teams")}
          className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
          data-testid="btn-back-to-teams"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to teams
        </button>

        {/* Header — team name + meta + view toggle */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h1
                className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight"
                data-testid="team-detail-name"
              >
                {team.name}
              </h1>
              {!team.isActive && (
                <Badge
                  variant="outline"
                  className="text-slate-500 border-slate-300"
                  data-testid="team-archived-badge"
                >
                  Archived
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="text-slate-600">{team.ageGroup}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-600">{team.ageRange}</span>
              <Badge
                className={cn("border ml-1", skillBadgeColor(team.skillLevel))}
                data-testid="team-skill-badge"
              >
                {team.skillLevel ?? "Unspecified"}
              </Badge>
            </div>
          </div>
          <ViewModeToggle />
        </div>

        {/* Common team info card — present in both views */}
        <div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          data-testid="team-info-card"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoRow icon={<Calendar className="h-4 w-4 text-slate-400" />} label="Schedule" value={team.schedule || "—"} />
            <InfoRow icon={<MapPin className="h-4 w-4 text-slate-400" />} label="Venue" value={team.venue || "—"} />
            <InfoRow
              icon={<User className="h-4 w-4 text-slate-400" />}
              label="Coach"
              value={`Coach ID #${team.coachId}`}
            />
            <InfoRow
              icon={<Trophy className="h-4 w-4 text-slate-400" />}
              label="Enrollment"
              value={`${team.currentEnrollment} of ${team.maxCapacity ?? "—"}`}
            />
          </div>
        </div>

        {/* View-specific body */}
        {viewMode === "practice" ? (
          <PracticeView team={team} />
        ) : (
          <GamedayView team={team} />
        )}
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}

// ─── Practice view shell ────────────────────────────────────────────────
function PracticeView({ team }: { team: BatchDetail }) {
  return (
    <div className="space-y-4" data-testid="view-practice">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">
          Practice mode
        </Badge>
      </div>

      {/* Roster placeholder — populated in PR-X3a (player_batches) */}
      <div
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        data-testid="practice-roster-placeholder"
      >
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Roster</h2>
        <p className="text-sm text-slate-600">
          Player assignment to this team will be available in the next release.
          You'll be able to filter players by age bracket and skill level when
          building the roster.
        </p>
      </div>

      {/* Practice-mode action stubs — real handlers stay on the LIST page
          for PR-X2 to keep mutation state in one place. Disabled buttons
          here are a UX hint that these actions exist for the team. */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled data-testid="btn-edit-team-detail">
          <Edit2 className="h-4 w-4 mr-2" />
          Edit Team (use list page)
        </Button>
        {team.isActive ? (
          <Button variant="outline" disabled data-testid="btn-archive-team-detail">
            <Archive className="h-4 w-4 mr-2" />
            Archive (use list page)
          </Button>
        ) : (
          <Button variant="outline" disabled data-testid="btn-restore-team-detail">
            <ArchiveRestore className="h-4 w-4 mr-2" />
            Restore (use list page)
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Edit / Archive / Restore live on the team list for PR-X2. Roster
        management and inline actions land in a follow-up.
      </p>
    </div>
  );
}

// ─── Game-day view shell ────────────────────────────────────────────────
function GamedayView({ team: _team }: { team: BatchDetail }) {
  return (
    <div className="space-y-4" data-testid="view-gameday">
      <div className="flex items-center gap-2">
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">
          Game-day mode
        </Badge>
      </div>

      {/* Lineup placeholder — implemented in PR-X3b (match_lineups).
          11 playing + 1 impact = 12 max, 11 min. Match metadata (opponent,
          date, venue-of-day) is ALSO PR-X3b — intentionally NOT in batches. */}
      <div
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        data-testid="gameday-lineup-placeholder"
      >
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Match Lineup</h2>
        <p className="text-sm text-slate-600 mb-4">
          Select the playing 11 + 1 impact player from this team's roster.
          Lineup building, opponent info, and match-day venue land in the
          next release.
        </p>
        <Button
          disabled
          data-testid="btn-build-lineup-disabled"
          title="Coming in next release"
        >
          Build lineup — coming soon
        </Button>
      </div>
    </div>
  );
}
